import gsap from 'gsap';
import { dur, prefersReducedMotion, type MotionActionHandle } from '../../game/motion';
import { isDismissSwipe, type SwipeSample } from './swipeUpDismissPolicy';

export interface SwipeUpDismissOptions {
  ondismiss: () => void;
}

/**
 * Pointer Events cover touch, pen and mouse with one path. The toast itself is
 * the drag surface while its parent remains owned by the enter/exit animation,
 * so a finger never fights GSAP for the same transform.
 */
export function swipeUpDismiss(
  node: HTMLElement,
  initial: SwipeUpDismissOptions,
): MotionActionHandle<SwipeUpDismissOptions> {
  let options = initial;
  let active: { pointerId: number; startX: number; startY: number; startedAt: number } | undefined;

  const setY = gsap.quickSetter(node, 'y', 'px');
  const setOpacity = gsap.quickSetter(node, 'opacity');

  function reset(): void {
    active = undefined;
    gsap.killTweensOf(node);
    gsap.to(node, {
      y: 0,
      opacity: 1,
      duration: dur(0.18),
      ease: 'power2.out',
      clearProps: 'opacity,transform',
    });
  }

  function onPointerDown(event: PointerEvent): void {
    if (!event.isPrimary || event.button !== 0) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('button, a, input, select, textarea')) return;

    gsap.killTweensOf(node);
    active = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startedAt: event.timeStamp,
    };
    node.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent): void {
    if (!active || event.pointerId !== active.pointerId) return;
    const deltaY = Math.min(0, event.clientY - active.startY);
    const distance = Math.abs(deltaY);

    setY(deltaY);
    setOpacity(Math.max(0.35, 1 - distance / Math.max(node.offsetHeight * 1.15, 1)));
  }

  function onPointerUp(event: PointerEvent): void {
    if (!active || event.pointerId !== active.pointerId) return;
    const sample: SwipeSample = {
      deltaX: event.clientX - active.startX,
      deltaY: event.clientY - active.startY,
      elapsedMs: event.timeStamp - active.startedAt,
    };
    active = undefined;

    if (node.hasPointerCapture(event.pointerId)) node.releasePointerCapture(event.pointerId);
    if (isDismissSwipe(sample)) {
      if (prefersReducedMotion()) gsap.set(node, { opacity: 0 });
      options.ondismiss();
      return;
    }
    reset();
  }

  function onPointerCancel(event: PointerEvent): void {
    if (active?.pointerId !== event.pointerId) return;
    reset();
  }

  node.addEventListener('pointerdown', onPointerDown);
  node.addEventListener('pointermove', onPointerMove);
  node.addEventListener('pointerup', onPointerUp);
  node.addEventListener('pointercancel', onPointerCancel);

  return {
    update(next) {
      options = next;
    },
    destroy() {
      gsap.killTweensOf(node);
      node.removeEventListener('pointerdown', onPointerDown);
      node.removeEventListener('pointermove', onPointerMove);
      node.removeEventListener('pointerup', onPointerUp);
      node.removeEventListener('pointercancel', onPointerCancel);
    },
  };
}
